import React, { useState, useEffect } from 'react'
import {
    Box,
    Button,
    Chip,
    Checkbox,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    IconButton,
    makeStyles,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
    MenuItem,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import SearchIcon from '@material-ui/icons/Search'
import { useQueryClient } from '@tanstack/react-query'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
} from '../../../ContractManager/CatalogFilterChrome'
import { useOrganizationCertificateTypes } from '../../../../hooks/useOrganizationCertificateTypes'
import { useOrganizationContractTypes } from '../../../../hooks/useOrganizationContractTypes'
import {
    Company,
    OrganizationContractType,
} from '../../../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        height: 'calc(100vh - 96px)',
        maxHeight: 720,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        overflow: 'hidden',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        flexWrap: 'wrap',
    },
    toolbarText: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.25),
        minWidth: 160,
    },
    toolbarControls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        flex: 1,
        justifyContent: 'flex-end',
    },
    searchField: {
        minWidth: 220,
        flex: '1 1 200px',
        maxWidth: 320,
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    filterField: {
        minWidth: 150,
        backgroundColor: theme.palette.background.paper,
    },
    typeRow: {
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    tablePaper: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
    },
    tableWrap: {
        height: '100%',
        overflow: 'auto',
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[100],
    },
    nameCell: {
        fontWeight: 600,
    },
    descriptionText: {
        color: theme.palette.text.secondary,
        maxWidth: 280,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    emptyState: {
        padding: theme.spacing(6, 2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        minWidth: 400,
    },
    requiredPropertiesBox: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
    },
    requiredPropertiesList: {
        maxHeight: 180,
        overflowY: 'auto',
        marginTop: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    propertyRow: {
        padding: theme.spacing(0.75, 1),
        '&:not(:last-child)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    selectAllRow: {
        padding: theme.spacing(0.75, 1),
        marginTop: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
}))

interface OrganizationContractTypesModalProps {
    open: boolean
    onClose: () => void
    organizationId: number
    propertyList: Company[]
    catalog?: 'contract' | 'certificate'
}

const emptyForm = {
    name: '',
    description: '',
    required_company_ids: [] as number[],
    is_active: true,
}

export const OrganizationContractTypesModal: React.FC<OrganizationContractTypesModalProps> = ({
    open,
    onClose,
    organizationId,
    propertyList,
    catalog = 'contract',
}) => {
    const classes = useStyles()
    const queryClient = useQueryClient()
    const [includeInactive, setIncludeInactive] = useState(false)
    const isCertificate = catalog === 'certificate'
    const noun = isCertificate ? 'certificate' : 'contract'

    const contractCatalog = useOrganizationContractTypes({
        organization: isCertificate ? null : organizationId,
        includeInactive,
    })
    const certificateCatalog = useOrganizationCertificateTypes({
        organization: isCertificate ? organizationId : null,
        includeInactive,
    })
    const { isLoading, createType, updateType, deleteType } = isCertificate
        ? certificateCatalog
        : contractCatalog
    const contractTypes = isCertificate
        ? certificateCatalog.certificateTypes
        : contractCatalog.contractTypes

    const [editorOpen, setEditorOpen] = useState(false)
    const [editing, setEditing] = useState<OrganizationContractType | null>(
        null,
    )
    const [form, setForm] = useState(emptyForm)
    const [propertySearch, setPropertySearch] = useState('')
    const [typeSearch, setTypeSearch] = useState('')
    const [requirementFilter, setRequirementFilter] = useState<
        'all' | 'required' | 'optional'
    >('all')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

    const filteredProperties = propertyList.filter((property) =>
        property.name
            .toLowerCase()
            .includes(propertySearch.trim().toLowerCase()),
    )
    const allFilteredSelected =
        filteredProperties.length > 0 &&
        filteredProperties.every((property) =>
            form.required_company_ids.includes(property.id),
        )
    const someFilteredSelected =
        filteredProperties.length > 0 &&
        filteredProperties.some((property) =>
            form.required_company_ids.includes(property.id),
        )

    const handleToggleAllFiltered = () => {
        setForm((f) => {
            if (allFilteredSelected) {
                const visibleIds = new Set(
                    filteredProperties.map((property) => property.id),
                )
                return {
                    ...f,
                    required_company_ids: f.required_company_ids.filter(
                        (id) => !visibleIds.has(id),
                    ),
                }
            }

            const nextIds = new Set(f.required_company_ids)
            filteredProperties.forEach((property) => nextIds.add(property.id))
            return {
                ...f,
                required_company_ids: Array.from(nextIds),
            }
        })
    }

    useEffect(() => {
        if (!open) {
            setEditorOpen(false)
            setEditing(null)
            setForm(emptyForm)
            setIncludeInactive(false)
            setPropertySearch('')
            setTypeSearch('')
            setRequirementFilter('all')
            setFilterDrawerOpen(false)
        }
    }, [open])

    const visibleTypes = contractTypes.filter((row) => {
        const requiredCount = row.required_company_ids?.length ?? 0
        if (requirementFilter === 'required' && requiredCount === 0) {
            return false
        }
        if (requirementFilter === 'optional' && requiredCount > 0) {
            return false
        }
        const search = typeSearch.trim().toLowerCase()
        if (!search) return true
        return (
            row.name.toLowerCase().includes(search) ||
            (row.description || '').toLowerCase().includes(search)
        )
    })

    const invalidateContracts = () => {
        queryClient.invalidateQueries({ queryKey: ['orgCompanyContracts'] })
        queryClient.invalidateQueries({ queryKey: ['companyContracts'] })
        queryClient.invalidateQueries({
            queryKey: ['organizationContractTypeCoverage'],
        })
        queryClient.invalidateQueries({
            queryKey: ['organizationCertificateTypeCoverage'],
        })
    }

    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setEditorOpen(true)
    }

    const openEdit = (row: OrganizationContractType) => {
        setEditing(row)
        setForm({
            name: row.name,
            description: row.description ?? '',
            required_company_ids: row.required_company_ids ?? [],
            is_active: row.is_active,
        })
        setPropertySearch('')
        setEditorOpen(true)
    }

    const handleSave = () => {
        const payload = {
            name: form.name.trim(),
            description: form.description.trim() || null,
            required_company_ids: form.required_company_ids,
            is_active: form.is_active,
        }
        if (!payload.name) return

        if (editing) {
            updateType.mutate(
                { id: editing.id, body: payload },
                {
                    onSuccess: () => {
                        setEditorOpen(false)
                        invalidateContracts()
                    },
                },
            )
        } else {
            createType.mutate(
                {
                    organization: organizationId,
                    ...payload,
                },
                {
                    onSuccess: () => {
                        setEditorOpen(false)
                        invalidateContracts()
                    },
                },
            )
        }
    }

    const handleDelete = (row: OrganizationContractType) => {
        if (
            !window.confirm(
                `Delete ${noun} type "${row.name}"? Existing ${noun}s are not deleted.`,
            )
        ) {
            return
        }
        deleteType.mutate(row.id, {
            onSuccess: () => invalidateContracts(),
        })
    }

    const toggleActive = (row: OrganizationContractType) => {
        updateType.mutate(
            { id: row.id, body: { is_active: !row.is_active } },
            { onSuccess: () => invalidateContracts() },
        )
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ className: classes.dialogPaper }}
            >
                <DialogTitle disableTypography>
                    <Box className={classes.header}>
                        <Box className={classes.titleBlock}>
                            <Typography variant="h6" component="span">
                                {isCertificate
                                    ? 'Certificate types'
                                    : 'Contract types'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Create categories, then mark which properties
                                must keep that {noun} on file.
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeInactive}
                                        onChange={(e) =>
                                            setIncludeInactive(e.target.checked)
                                        }
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label="Show inactive"
                            />
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent className={classes.content}>
                    <Box className={classes.toolbar}>
                        <Box className={classes.toolbarText}>
                            <Typography variant="subtitle2">
                                {visibleTypes.length} of {contractTypes.length}{' '}
                                type{contractTypes.length === 1 ? '' : 's'}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Requirements can vary by property.
                            </Typography>
                        </Box>
                        <Box className={classes.toolbarControls}>
                            <Box style={{ flex: 1, minWidth: 240 }}>
                                <CatalogFilterBar
                                    searchPlaceholder={`Search ${noun} types...`}
                                    searchValue={typeSearch}
                                    onSearchChange={setTypeSearch}
                                    onOpenFilters={() =>
                                        setFilterDrawerOpen(true)
                                    }
                                    chips={[
                                        ...(requirementFilter !== 'all'
                                            ? [
                                                  {
                                                      id: 'requirement',
                                                      label:
                                                          requirementFilter ===
                                                          'required'
                                                              ? 'Required somewhere'
                                                              : 'Optional only',
                                                      onDelete: () =>
                                                          setRequirementFilter(
                                                              'all',
                                                          ),
                                                  },
                                              ]
                                            : []),
                                        ...(includeInactive
                                            ? [
                                                  {
                                                      id: 'inactive',
                                                      label: 'Showing inactive',
                                                      onDelete: () =>
                                                          setIncludeInactive(
                                                              false,
                                                          ),
                                                  },
                                              ]
                                            : []),
                                    ]}
                                    onClearAll={
                                        requirementFilter !== 'all' ||
                                        includeInactive
                                            ? () => {
                                                  setRequirementFilter('all')
                                                  setIncludeInactive(false)
                                              }
                                            : undefined
                                    }
                                />
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={openCreate}
                            >
                                Add type
                            </Button>
                        </Box>
                    </Box>
                    <Paper variant="outlined" className={classes.tablePaper}>
                        <TableContainer className={classes.tableWrap}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Name
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Description
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Required properties
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                        >
                                            Active
                                        </TableCell>
                                        <TableCell
                                            className={classes.headerCell}
                                            align="right"
                                        >
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Box
                                                    className={
                                                        classes.emptyState
                                                    }
                                                >
                                                    Loading {noun} types…
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : visibleTypes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Box
                                                    className={
                                                        classes.emptyState
                                                    }
                                                >
                                                    <Typography variant="subtitle2">
                                                        {contractTypes.length ===
                                                        0
                                                            ? `No ${noun} types yet`
                                                            : `No ${noun} types match these filters`}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        {contractTypes.length ===
                                                        0
                                                            ? `Add a type to group ${noun}s and mark required properties.`
                                                            : 'Clear the search or switch the requirement filter.'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        visibleTypes.map((row) => (
                                            <TableRow
                                                key={row.id}
                                                className={classes.typeRow}
                                            >
                                                <TableCell
                                                    className={classes.nameCell}
                                                >
                                                    {row.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip
                                                        title={
                                                            row.description ||
                                                            ''
                                                        }
                                                        disableHoverListener={
                                                            !row.description
                                                        }
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            className={
                                                                classes.descriptionText
                                                            }
                                                        >
                                                            {row.description ||
                                                                '—'}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            row
                                                                .required_company_ids
                                                                .length > 0
                                                                ? `${row.required_company_ids.length} required`
                                                                : 'Optional'
                                                        }
                                                        color={
                                                            row
                                                                .required_company_ids
                                                                .length > 0
                                                                ? 'primary'
                                                                : 'default'
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        style={{ gap: 8 }}
                                                    >
                                                        <Switch
                                                            checked={
                                                                row.is_active
                                                            }
                                                            onChange={() =>
                                                                toggleActive(
                                                                    row,
                                                                )
                                                            }
                                                            size="small"
                                                            color="primary"
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            {row.is_active
                                                                ? 'Active'
                                                                : 'Inactive'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                openEdit(row)
                                                            }
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        row,
                                                                    )
                                                                }
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="default">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title={`${
                    isCertificate ? 'Certificate' : 'Contract'
                } type filters`}
                activeCount={
                    (requirementFilter !== 'all' ? 1 : 0) +
                    (includeInactive ? 1 : 0)
                }
                onClear={() => {
                    setRequirementFilter('all')
                    setIncludeInactive(false)
                }}
            >
                <TextField
                    label="Requirement"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={requirementFilter}
                    onChange={(e) =>
                        setRequirementFilter(
                            e.target.value as 'all' | 'required' | 'optional',
                        )
                    }
                >
                    <MenuItem value="all">All types</MenuItem>
                    <MenuItem value="required">Required somewhere</MenuItem>
                    <MenuItem value="optional">Optional</MenuItem>
                </TextField>
                <FormControlLabel
                    control={
                        <Switch
                            checked={includeInactive}
                            onChange={(e) =>
                                setIncludeInactive(e.target.checked)
                            }
                            color="primary"
                            size="small"
                        />
                    }
                    label="Show inactive types"
                />
            </CatalogFilterDrawer>

            <Dialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editing ? `Edit ${noun} type` : `New ${noun} type`}
                </DialogTitle>
                <DialogContent>
                    <Box className={classes.form} marginTop={1}>
                        <TextField
                            label="Name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            value={form.name}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, name: e.target.value }))
                            }
                        />
                        <TextField
                            label="Description (optional)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={form.description}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    description: e.target.value,
                                }))
                            }
                        />
                        <Box className={classes.requiredPropertiesBox}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Typography variant="subtitle2">
                                    Required properties
                                </Typography>
                                <Chip
                                    size="small"
                                    color={
                                        form.required_company_ids.length > 0
                                            ? 'primary'
                                            : 'default'
                                    }
                                    label={
                                        form.required_company_ids.length > 0
                                            ? `${form.required_company_ids.length} selected`
                                            : 'Optional'
                                    }
                                />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                                Select the properties that require this {noun}{' '}
                                type.
                            </Typography>
                            <TextField
                                placeholder="Search properties..."
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={propertySearch}
                                onChange={(e) =>
                                    setPropertySearch(e.target.value)
                                }
                                style={{ marginTop: 12 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Box className={classes.selectAllRow}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allFilteredSelected}
                                            indeterminate={
                                                someFilteredSelected &&
                                                !allFilteredSelected
                                            }
                                            onChange={handleToggleAllFiltered}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        propertySearch.trim()
                                            ? `Select all (${filteredProperties.length} shown)`
                                            : 'Select all properties'
                                    }
                                />
                            </Box>
                            <Box className={classes.requiredPropertiesList}>
                                {filteredProperties.map((property) => (
                                    <Box
                                        key={property.id}
                                        className={classes.propertyRow}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.required_company_ids.includes(
                                                        property.id,
                                                    )}
                                                    onChange={(e) =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            required_company_ids: e
                                                                .target.checked
                                                                ? [
                                                                      ...f.required_company_ids,
                                                                      property.id,
                                                                  ]
                                                                : f.required_company_ids.filter(
                                                                      (id) =>
                                                                          id !==
                                                                          property.id,
                                                                  ),
                                                        }))
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={property.name}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.is_active}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            is_active: e.target.checked,
                                        }))
                                    }
                                    color="primary"
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setEditorOpen(false)}
                        color="default"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={
                            !form.name.trim() ||
                            createType.isLoading ||
                            updateType.isLoading
                        }
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
